import { Suspense, useEffect } from 'react';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import ErrorBoundary from '@components/shared/ErrorBoundary/ErrorBoundary';
import RouteSkeleton from '@components/shared/RouteSkeleton/RouteSkeleton';
import { getEmployeeById } from '@/services/employeeService';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/e')({
  component: EmployeeLayout,
});

function EmployeeLayout() {
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const userType = useAuthStore((s) => s.userType);
  const employeeId = session?.user.employeeId ?? '';
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Org type guard — only EMPLOYEE users can access /e routes
  useEffect(() => {
    if (!userType) return;
    if (userType !== 'EMPLOYEE') {
      void navigate({ to: '/', replace: true });
    }
  }, [userType, navigate]);

  const { data: employeeData, isLoading } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployeeById(employeeId),
    enabled: !!employeeId,
    staleTime: 30_000,
  });

  const status = employeeData?.status;

  // Status-based routing — redirect to correct child route
  useEffect(() => {
    if (isLoading || !status) return;

    if (status === 'PENDING_ONBOARDING' && pathname !== '/e/employee-onboarding') {
      void navigate({ to: '/e/employee-onboarding', replace: true });
    } else if (status === 'ACTIVATION_PENDING' && pathname !== '/e/employee-complete') {
      void navigate({ to: '/e/employee-complete', replace: true });
    } else if (
      status === 'ACTIVE' &&
      (pathname === '/e/employee-onboarding' || pathname === '/e/employee-complete')
    ) {
      void navigate({ to: '/e', replace: true });
    }
  }, [isLoading, status, pathname, navigate]);

  // Block render until the auth store is hydrated — see w.tsx for the
  // same-frame leak rationale.
  if (!userType) return null;
  if (userType !== 'EMPLOYEE') return null;
  if (isLoading) return null;

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}
